import { useState } from 'react'
import type { FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../../lib/api'
import { useCapabilities } from '../../lib/capabilities'
import { titleCase } from '../../lib/format'
import type { HiredEquipment, ListResponse } from '../../types'
import {
  Alert, Button, Card, CardBody, CardHeader, EmptyState, Input, Select, Skeleton, StatusBadge,
} from '../../components/ui'
import { Wizard } from '../../components/Wizard'
import { CloseIcon, PlusIcon } from '../../components/icons'

const EQUIPMENT_TYPES = ['excavator', 'loader', 'drill_rig', 'compressor', 'generator', 'pump', 'other']

export default function Equipment() {
  const qc = useQueryClient()
  const [showForm, setShowForm] = useState(false)

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['leader', 'equipment'],
    queryFn: () => api.get<ListResponse<HiredEquipment>>('/leader/load-equipment'),
  })

  const caps = useCapabilities()

  // Dispatchers record equipment wherever it turns up, including for
  // another cooperative under the same parent - so they get a picker.
  // Everyone else records against their own org and never sees this.
  const canPickOrg = caps.has('*') || caps.has('dispatch.manage')
  const orgsQuery = useQuery({
    queryKey: ['orgs', 'for-equipment'],
    queryFn: () => api.get<{ organizations: { id?: string; name?: string }[] }>(
      '/organizations?limit=200'),
    enabled: canPickOrg,
  })

  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post('/leader/load-equipment', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['leader', 'equipment'] })
      setShowForm(false)
    },
  })

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = new FormData(e.currentTarget)
    const rate = form.get('daily_rate')
    create.mutate({
      // Empty means "my own cooperative" - the server defaults to it.
      org_id: String(form.get('org_id') ?? ''),
      name: String(form.get('name') ?? '').trim(),
      equipment_type: String(form.get('equipment_type') ?? 'other'),
      ownership: String(form.get('ownership') ?? 'hired'),
      supplier_name: String(form.get('supplier_name') ?? '').trim(),
      daily_rate: rate ? Number(rate) : undefined,
    })
  }

  const items = data?.items ?? []

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Hired equipment</h1>
          <p className="page-head__sub">Excavators, loaders, drill rigs and other hired plant.</p>
        </div>
        <Button className="fab-trigger__inline" icon={<PlusIcon />} onClick={() => setShowForm((s) => !s)}>
          {showForm ? 'Cancel' : 'Add equipment'}
        </Button>
        <button
          type="button" className="fab"
          onClick={() => setShowForm((s) => !s)}
          aria-label={showForm ? 'Cancel' : 'Add equipment'}
        >
          {showForm ? <CloseIcon size={22} /> : <PlusIcon size={22} />}
        </button>
      </div>

      {showForm && (
        <Card style={{ marginBottom: 'var(--sp-6)' }}>
          <CardHeader title="New equipment entry" />
          <CardBody>
            <form onSubmit={onSubmit}>
              <div className="stack" style={{ gap: 'var(--sp-4)' }}>
                {create.isError && (
                  <Alert tone="danger">
                    {create.error instanceof ApiError ? create.error.message : 'Could not add that entry.'}
                  </Alert>
                )}
                {/* PWA: 2 guided steps; desktop stacked, unchanged. */}
                <Wizard
                  steps={[
                    ...(canPickOrg ? [{ title: 'Cooperative', content: (
                      <Select label="Recording for" name="org_id" defaultValue="">
                        <option value="">My own cooperative</option>
                        {(orgsQuery.data?.organizations ?? []).map((o) => (
                          <option key={o.id} value={o.id}>{o.name}</option>
                        ))}
                      </Select>
                    ) }] : []),
                    { title: 'Equipment', content: (
                      <div className="stack">
                        <Input label="Name" name="name" required />
                        <div className="grid grid--2">
                          <Select label="Type" name="equipment_type" defaultValue="other">
                            {EQUIPMENT_TYPES.map((t) => <option key={t} value={t}>{titleCase(t)}</option>)}
                          </Select>
                          <Select label="Ownership" name="ownership" defaultValue="hired">
                            <option value="hired">Hired</option>
                            <option value="owned">Owned</option>
                          </Select>
                        </div>
                      </div>
                    ) },
                    { title: 'Supplier & cost', content: (
                      <div className="grid grid--2">
                        <Input label="Supplier" name="supplier_name" />
                        <Input label="Daily rate (ZMW)" name="daily_rate" type="number" step="any" min="0" />
                      </div>
                    ) },
                  ]}
                  footer={(
                    <div className="row">
                      <Button type="submit" loading={create.isPending} confirm={{ title: "Add this equipment?" }}>Add equipment</Button>
                    </div>
                  )}
                />
              </div>
            </form>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader title="Equipment" sub={items.length ? `${items.length} on file` : undefined} />
        <CardBody flush>
          {isError ? (
            <div style={{ padding: 'var(--sp-6)' }}><Alert tone="danger">{(error as Error).message}</Alert></div>
          ) : isLoading ? (
            <div style={{ padding: 'var(--sp-6)' }} className="stack"><Skeleton h={20} /><Skeleton h={20} /></div>
          ) : items.length === 0 ? (
            <EmptyState title="No equipment yet" body="Add hired plant to track cost and availability." />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead><tr><th>Equipment</th><th>Type</th><th>Ownership</th><th>Supplier</th><th>Status</th></tr></thead>
                <tbody>
                  {items.map((e) => (
                    <tr key={e.id}>
                      <td style={{ fontWeight: 600 }}>{e.name}</td>
                      <td>{titleCase(e.equipment_type)}</td>
                      <td>{titleCase(e.ownership)}</td>
                      <td>{e.supplier_name || '—'}</td>
                      <td><StatusBadge status={e.status || 'active'} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </>
  )
}
